import DropdownSelect from "../../../../common/DropdownSelect";

import { useFormikContext } from "formik";
import { AddSolutionFormValues, InterventionWithCounter } from "../../../types";
import { Box, IconButton, SelectChangeEvent, Tooltip } from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { startCase } from "lodash";
import { actionOptions, ecosystemOptions } from "./inputOptions";

type ErrorPassed = string | { message: string; outcomeCounter?: number };
type ErrorItem = Partial<Record<string, ErrorPassed>>;
type TouchedItem = Partial<Record<string, boolean>>;

function Intervention({
  value,
  index,
}: {
  value: InterventionWithCounter;
  index: number;
}) {
  const context = useFormikContext<AddSolutionFormValues>();

  type TwoFieldNames = "action" | "ecosystem";
  const interventionFields: Array<{
    fieldName: TwoFieldNames;
    options: Array<string>;
  }> = [
    {
      fieldName: "action",
      options: actionOptions,
    },
    {
      fieldName: "ecosystem",
      options: ecosystemOptions,
    },
  ];

  const ensureStringArray = (value: unknown): Array<string> => {
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is string => typeof item === "string");
  };

  const getValueFromFormState = (fieldName: TwoFieldNames): Array<string> => {
    const intervention = context.values["interventions"][index];
    const field = ensureStringArray(intervention?.[fieldName]);
    return field;
  };

  const setValueAsFormState = (
    event: SelectChangeEvent<string | number | Array<string>>,
    fieldName: TwoFieldNames,
  ) => {
    const selectedValues = Array.isArray(event.target.value)
      ? event.target.value.map(String)
      : [String(event.target.value)];
    const path = `interventions[${index}].${fieldName}`;
    context.setFieldTouched(path, true, false);
    context.setFieldValue(path, selectedValues, true);
  };

  const getErrorFromFormState = (fieldName: string): string | undefined => {
    const touchedRow = (
      context.touched?.interventions as
        | Array<TouchedItem | undefined>
        | undefined
    )?.[index];
    const errorsRow = (
      context.errors?.interventions as Array<ErrorItem | undefined> | undefined
    )?.[index];

    const pairTouched =
      Boolean(touchedRow?.action) || Boolean(touchedRow?.ecosystem);
    if (!pairTouched) return undefined;

    const fieldError = errorsRow?.[fieldName];
    if (!fieldError) return undefined;
    if (typeof fieldError === "string") return fieldError;
    if (typeof fieldError === "object" && "message" in fieldError) {
      const maybeMessage = (fieldError as { message?: unknown }).message;
      return typeof maybeMessage === "string" ? maybeMessage : undefined;
    }
    return undefined;
  };

  const markTouched = (fieldName: string) => {
    context.setFieldTouched(`interventions[${index}].${fieldName}`, true, true);
  };

  const removeInterventionFromState = () => {
    const next = [...context.values.interventions];
    next.splice(index, 1);
    context.setFieldValue("interventions", next, true);
    const prevTouched =
      (context.touched?.interventions as Array<TouchedItem> | undefined) ?? [];
    const nextTouched = [...prevTouched];
    nextTouched.splice(index, 1);
    context.setTouched(
      { ...context.touched, interventions: nextTouched },
      false,
    );
  };
  return (
    <Box
      data-testid={`solution-intervention-container-${value.interventionCounter}`}
      sx={{
        display: "flex",
        direction: "column",
        justifyContent: "space-between",
      }}
    >
      <Box
        data-testid={`solution-intervention-container-dropdown-fields-${value.interventionCounter}`}
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "90%",
        }}
      >
        {interventionFields.map((intervention) => {
          const validationMessage = getErrorFromFormState(
            intervention.fieldName,
          );
          return (
            <DropdownSelect
              key={intervention.fieldName}
              options={intervention.options}
              value={getValueFromFormState(intervention.fieldName)}
              onChange={(
                event: SelectChangeEvent<string | number | string[]>,
              ) => setValueAsFormState(event, intervention.fieldName)}
              label={startCase(intervention.fieldName)}
              onBlur={() => markTouched(intervention.fieldName)}
              id={`solution-intervention-${intervention.fieldName}-${value.interventionCounter}`}
              multiple
              error={Boolean(validationMessage)}
              helperText={validationMessage}
              name={`interventions[${index}].${intervention.fieldName}`}
            />
          );
        })}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <Tooltip title="Delete intervention">
          <IconButton
            onClick={removeInterventionFromState}
            sx={{
              padding: 0,
            }}
            aria-label={`delete-intervention-${value.interventionCounter}`}
            size="large"
          >
            <DeleteForeverIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default Intervention;
